<template>
  <label class="ux-field-title" v-if="showLabel"
    >{{ label }}
    <span class="ux-required-marker" v-if="required">*</span>
  </label>
  <div class="ux-field-description" v-if="showDescription">
    {{ description }}
  </div>

  <!-- Currently selected items -->
  <template v-if="showList">
    <template v-if="multiValue">
      <template v-if="model && model.length">
        <draggable class="items" v-model="model">
          <template #item="{ element }">
            <item :key="element._id" :item="element">
              <template #actions>
                <flex-row gap center>
                  <flex-cell>
                    <ux-menu group="content-actions" right>
                      <template #activator="{ on }">
                        <ux-button size="sm" v-on="on" icon @click.stop.prevent>
                          <ux-icon icon="fa-ellipsis" />
                        </ux-button>
                      </template>
                      <ux-list>
                        <ux-list-item @click="remove(element)">
                          Remove {{ elementLabel(element) }}
                        </ux-list-item>
                        <ux-list-item
                          v-if="canEdit(element)"
                          @click="edit(element)"
                        >
                          Edit {{ elementLabel(element) }}
                        </ux-list-item>
                        <ux-list-item @click="$actions.open([element])">
                          More Actions
                        </ux-list-item>
                      </ux-list>
                    </ux-menu>
                  </flex-cell>
                  <flex-cell>
                    <ux-button
                      size="sm"
                      v-on="on"
                      icon
                      @click.stop.prevent="remove(element)"
                    >
                      <ux-icon icon="fa-times" />
                    </ux-button>
                  </flex-cell>
                </flex-row>
              </template>
            </item>
          </template>
        </draggable>
      </template>
    </template>
    <template v-else-if="model">
      <div class="items">
        <item :item="model">
          <template #actions>
            <ux-menu group="content-actions" right>
              <template #activator="{ on }">
                <ux-button size="sm" v-on="on" icon @click.stop.prevent>
                  <ux-icon icon="fa-ellipsis" />
                </ux-button>
              </template>
              <ux-list>
                <ux-list-item v-if="canCreate" @click="createNew">
                  Replace with new
                </ux-list-item>
                <ux-list-item @click="swap">Swap with existing</ux-list-item>
                <ux-list-item @click="clear">Remove</ux-list-item>
                <ux-list-item v-if="canEdit(model)" @click="edit(model)">
                  Edit
                </ux-list-item>
                <ux-list-item @click="$actions.open([model])">
                  More Actions
                </ux-list-item>
              </ux-list>
            </ux-menu>
          </template>
        </item>
      </div>
    </template>
  </template>

  <!-- Combobox: search left, browse/create inside the panel -->
  <div class="content-picker" v-if="canAdd" ref="picker">
    <button
      v-if="!panelOpen"
      type="button"
      class="picker-trigger"
      @click="openPanel"
    >
      <ux-icon icon="fa-search" class="picker-trigger-icon" />
      <span class="picker-trigger-label">{{ triggerLabel }}</span>
      <ux-icon icon="fa-angle-down" class="picker-trigger-caret" />
    </button>

    <template v-else>
      <div class="picker-input-wrap" ref="inputWrap">
        <ux-icon
          :icon="searching || loadingSuggestions ? 'fa-spinner' : 'fa-search'"
          :spin="searching || loadingSuggestions"
          class="picker-input-icon"
        />
        <input
          ref="searchInput"
          class="picker-input"
          v-model="keywords"
          :placeholder="placeholderText"
          @keydown="handleKeydown"
        />
        <!-- <ux-icon
          v-if="searching || loadingSuggestions"
          icon="fa-spinner"
          spin
          class="picker-input-spinner"
        /> -->
      </div>

      <teleport to="body">
        <div class="picker-panel-outer" ref="panelOuter" :style="panelStyle">
          <ux-panel class="picker-panel">
            <div
              class="picker-section-label"
              v-if="displayResults.length && !keywords.trim()"
            >
              Suggestions
            </div>

            <div class="picker-results" v-if="displayResults.length">
              <item
                v-for="(option, index) in displayResults"
                :key="option._id || index"
                :item="option"
                manual
                :class="{ 'nav-active': index === activeIndex }"
                @clicked="selectItem"
                @mouseover="activeIndex = index"
              />
            </div>

            <div
              class="picker-status"
              v-else-if="searching || loadingSuggestions"
            >
              Searching…
            </div>
            <!-- <div class="picker-status" v-else>
          {{
            keywords.trim()
              ? `No matches for "${keywords.trim()}"`
              : `No ${pluralLabel} to suggest`
          }}
        </div> -->
            <!-- <button
          v-if="multiValue"
          type="button"
          class="picker-browse-lead"
          :class="{ 'nav-active': activeIndex === browseNavIndex }"
          @click="browseModal"
          @mouseover="activeIndex = browseNavIndex"
        >
          <ux-icon icon="fa-search" />
          <span>Advanced Search {{ pluralLabel }}</span>
          <ux-icon icon="fa-arrow-right" />
        </button> -->

            <!-- v-if="!multiValue || canCreate" -->
            <div class="picker-footer">
              <ux-button
                size="sm"
                :class="{ 'nav-active': activeIndex === browseNavIndex }"
                @click="browseModal"
                @mouseover="activeIndex = browseNavIndex"
              >
                Advanced Search
                <ux-icon icon="fa-search" right />
              </ux-button>
              <flex-spacer />
              <ux-button
                v-if="canCreate"
                size="sm"
                color="primary"
                :class="{ 'nav-active': activeIndex === createNavIndex }"
                @click="createNew"
                @mouseover="activeIndex = createNavIndex"
              >
                Create new
                <ux-icon icon="fa-plus" right />
              </ux-button>
            </div>
          </ux-panel>
        </div>
      </teleport>
    </template>
  </div>
</template>
<script>
import Item from "../../content/item.vue";
import InputMixin from "./input-mixin";
import draggable from "vuedraggable/src/vuedraggable";
import _debounce from "lodash/debounce";

const SUGGESTION_LIMIT = 4;
const RESULT_LIMIT = 5;

function sortByTitle(a, b) {
  const nameA = (a.title || "").toUpperCase();
  const nameB = (b.title || "").toUpperCase();
  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  return 0;
}

export default {
  components: {
    Item,
    draggable,
  },
  mixins: [InputMixin],
  props: {
    modelValue: {
      type: [Object, Array],
    },
  },
  data() {
    return {
      panelOpen: false,
      keywords: "",
      results: [],
      suggestions: [],
      suggestionsLoaded: false,
      loadingSuggestions: false,
      searching: false,
      activeIndex: -1,
      panelStyle: {},
      panelZIndex: null,
      panelResizeObserver: null,
    };
  },
  async created() {
    // Built synchronously before the first await so the keywords watcher
    // always has it available.
    this.runSearch = _debounce(this.runSearchImmediate, 300);

    await this.$glossary.ensure();
    this.value = this.cleanInput(this.value, true);
    this.dispatch();
  },
  mounted() {
    // Capture phase: selected-item rows (and other inner elements) use
    // `@click.stop`, which would swallow a bubble-phase listener before it
    // runs. Capturing lets us detect the outside-click before `.stop` fires —
    // so clicking a selected item (which opens a preview modal) closes the
    // still-open panel instead of leaving it floating over the new modal.
    document.addEventListener("click", this.handleDocumentClick, true);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick, true);
    this.removePanelListeners();
    if (this.runSearch && this.runSearch.cancel) {
      this.runSearch.cancel();
    }
  },
  watch: {
    keywords(kw) {
      this.activeIndex = -1;
      const trimmed = (kw || "").trim();

      if (!trimmed) {
        this.results = [];
        this.searching = false;
        if (this.runSearch && this.runSearch.cancel) {
          this.runSearch.cancel();
        }
        return;
      }

      this.searching = true;
      this.runSearch(trimmed);
    },
  },
  computed: {
    referenceType() {
      return this.field.referenceType;
    },
    definition() {
      return this.$glossary.hash[this.referenceType];
    },
    basicType() {
      // The underlying basic type (e.g. "image") — referenceType may be a
      // defined type that extends it.
      return this.definition?.definesType || this.referenceType;
    },
    isTouchDevice() {
      // Touch devices pop a virtual keyboard on autofocus — skip it there.
      if (typeof window === "undefined" || !window.matchMedia) {
        return false;
      }
      return window.matchMedia("(pointer: coarse)").matches;
    },
    pluralLabel() {
      return this.definition?.plural || "items";
    },
    singularLabel() {
      return this.definition?.title || "item";
    },
    placeholderText() {
      return `Search ${this.pluralLabel}…`;
    },
    triggerLabel() {
      if (this.multiValue && this.numValues) {
        return `Add another ${this.singularLabel}…`;
      }
      return `Search or browse ${this.pluralLabel}…`;
    },
    selectedIds() {
      const set = new Set();
      const m = this.model;
      if (Array.isArray(m)) {
        m.forEach((entry) => {
          if (entry) set.add(entry._id || entry);
        });
      } else if (m) {
        set.add(m._id || m);
      }
      return set;
    },
    displayResults() {
      const base = this.keywords.trim() ? this.results : this.suggestions;
      // Items already selected shouldn't reappear as pickable options.
      if (!this.selectedIds.size) return base;
      return base.filter((item) => !this.selectedIds.has(item._id || item));
    },
    showList() {
      return this.field.list !== false;
    },
    canAdd() {
      if (!this.showList) return true;
      if (!this.maximum) return true;
      return this.multiValue ? this.model.length < this.maximum : !this.model;
    },
    dateFilterEnabled() {
      switch (this.referenceType) {
        case "log":
        case "event":
        case "submission":
        case "email":
        case "transaction":
          return true;
      }
      return false;
    },
    canCreate() {
      let basicType = this.referenceType;
      if (basicType === "scope") return false;
      if (this.field.create === false) return false;
      if (!this.$sdk.global?.create) return false;

      let definedType = basicType;
      const match = this.$glossary.hash[this.referenceType];
      if (match) {
        basicType = match.definesType || match.key;
        definedType = match.key || match.definesType;
      }

      return (
        this.user &&
        this.$sdk.access.canCreate(this.user, definedType, basicType)
      );
    },
    // Unified ordered list of keyboard-navigable entries, in visual order:
    // each result, then Browse, then Create.
    navItems() {
      const items = this.displayResults.map((item) => ({
        type: "result",
        item,
      }));
      items.push({ type: "browse" });
      if (this.canCreate) {
        items.push({ type: "create" });
      }
      return items;
    },
    browseNavIndex() {
      return this.displayResults.length;
    },
    createNavIndex() {
      return this.canCreate ? this.navItems.length - 1 : -1;
    },
  },
  methods: {
    elementLabel(element) {
      const str = element.firstName || element.title || "";
      return str.length < 60 ? str : "";
    },
    normalizeItem(item) {
      // The search endpoint returns minimal docs ({ _id, title }). The <item>
      // component keys its thumbnail/avatar rendering off meta.type, and
      // every result here is of this field's reference type — so stamp that
      // on when it's missing. <item>/ux-image render the thumbnail from _id.
      if (item && item._id && !item.meta?.type && this.basicType) {
        return {
          ...item,
          meta: { ...(item.meta || {}), type: this.basicType },
        };
      }
      return item;
    },
    openPanel() {
      // Polymorphic reference (no fixed type) — search can't help, browse.
      if (!this.referenceType) {
        this.browseModal();
        return;
      }

      this.panelOpen = true;
      this.touch();
      this.loadSuggestions();

      // Grab a stacking index above whatever's currently on screen (e.g. the
      // modal the picker lives inside) once, at open — not on every reposition.
      this.panelZIndex = this.getMaxZIndex();

      this.$nextTick(() => {
        this.positionPanel();
        this.addPanelListeners();
        if (!this.isTouchDevice) {
          this.$refs.searchInput?.focus();
        }
      });
    },
    closePanel() {
      this.removePanelListeners();
      this.panelOpen = false;
      this.keywords = "";
      this.results = [];
      this.activeIndex = -1;
    },
    positionPanel() {
      // The panel is teleported to <body> to escape any overflow-clipping
      // ancestor (a modal, a panel with overflow:hidden, etc.). That means it
      // no longer inherits the input's box, so we drive its position from the
      // input's viewport rect and keep it in sync on scroll/resize.
      const anchor = this.$refs.inputWrap;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const style = {
        position: "fixed",
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: this.panelZIndex,
      };

      // Flip above the input when there isn't room below and there's more room
      // above — fixed positioning can otherwise spill off the bottom edge.
      const outer = this.$refs.panelOuter;
      const panelHeight = outer ? outer.offsetHeight : 0;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (
        panelHeight &&
        spaceBelow < panelHeight + 4 &&
        spaceAbove > spaceBelow
      ) {
        style.top = `${rect.top - panelHeight - 4}px`;
      } else {
        style.top = `${rect.bottom + 4}px`;
      }

      this.panelStyle = style;
    },
    addPanelListeners() {
      // Capture-phase scroll catches scrolling of ANY ancestor (e.g. a modal
      // body the picker lives inside), so the panel tracks the input rather
      // than detaching from it.
      window.addEventListener("scroll", this.positionPanel, true);
      window.addEventListener("resize", this.positionPanel);
      this.$nextTick(() => {
        const el = this.$refs.panelOuter;
        if (el && typeof ResizeObserver !== "undefined") {
          // Reposition (and re-evaluate the flip) as the panel's height changes
          // — async suggestions/results load in after open.
          this.panelResizeObserver = new ResizeObserver(this.positionPanel);
          this.panelResizeObserver.observe(el);
        }
      });
    },
    removePanelListeners() {
      window.removeEventListener("scroll", this.positionPanel, true);
      window.removeEventListener("resize", this.positionPanel);
      if (this.panelResizeObserver) {
        this.panelResizeObserver.disconnect();
        this.panelResizeObserver = null;
      }
    },
    getMaxZIndex() {
      return Math.max(
        ...Array.from(document.querySelectorAll("body *"), (el) =>
          parseFloat(window.getComputedStyle(el).zIndex),
        ).filter((zIndex) => !Number.isNaN(zIndex)),
        0,
      );
    },
    handleDocumentClick(event) {
      if (!this.panelOpen) return;
      const root = this.$refs.picker;
      if (!root) return;
      // Use composedPath() rather than root.contains(event.target): the click
      // that opens the panel replaces the trigger button, so event.target is
      // detached from the DOM by the time this handler runs — a contains()
      // check would then wrongly read the opening click as an outside click
      // and immediately close the panel. composedPath() is captured at
      // dispatch time and is immune to that mid-dispatch DOM change.
      const path = event.composedPath();
      // The panel is teleported to <body>, so it's no longer inside `root` —
      // a click on a result (or the footer buttons) would otherwise read as
      // "outside" and close the panel before the click registers. Treat the
      // teleported panel as inside too.
      const panel = this.$refs.panelOuter;
      if (path.includes(root)) return;
      if (panel && path.includes(panel)) return;
      this.closePanel();
    },
    async loadSuggestions() {
      if (this.suggestionsLoaded || !this.referenceType) return;
      this.suggestionsLoaded = true;
      this.loadingSuggestions = true;

      try {
        // No `select` projection: full items are needed so <item> can render
        // thumbnails/avatars and so a picked item carries complete data.
        const criteria = {
          databaseLimit: SUGGESTION_LIMIT,
          sort: { direction: "dsc", key: "meta.updated", type: "date" },
        };
        if (this.field.lockFilter) {
          criteria.filter = this.field.lockFilter;
        }
        const { items } = await this.$sdk.content.list(
          this.referenceType,
          criteria,
        );
        this.suggestions = (items || []).map((i) => this.normalizeItem(i));
      } catch (err) {
        // Allow a later retry if the suggestion fetch failed.
        this.suggestionsLoaded = false;
      } finally {
        this.loadingSuggestions = false;
      }
    },
    async runSearchImmediate(search) {
      if (!this.referenceType) {
        this.searching = false;
        return;
      }

      try {
        const criteria = { search, page: { size: RESULT_LIMIT } };
        if (this.field.lockFilter) {
          criteria.filter = this.field.lockFilter;
        }
        const { items } = await this.$sdk.content.list(
          this.referenceType,
          criteria,
        );

        // Ignore responses for a search term the user has moved on from.
        if (this.keywords.trim() === search) {
          this.results = (items || []).map((i) => this.normalizeItem(i));
          this.activeIndex = this.results.length ? 0 : -1;
        }
      } catch (err) {
        // swallow — a failed search just shows no matches
      } finally {
        if (this.keywords.trim() === search) {
          this.searching = false;
        }
      }
    },
    selectItem(option) {
      if (!option) return;
      this.touch();

      if (this.multiValue) {
        const current = Array.isArray(this.model) ? this.model : [];
        const exists = current.some(function (item) {
          return (item._id || item) === (option._id || option);
        });
        if (!exists) {
          this.model = [...current, option];
        }

        // Keep the panel open for the next pick.
        this.keywords = "";
        this.results = [];
        this.activeIndex = -1;
        if (!this.isTouchDevice) {
          this.$nextTick(() => this.$refs.searchInput?.focus());
        }
      } else {
        this.model = option;
        this.closePanel();
      }
    },
    handleKeydown(event) {
      const items = this.navItems;

      switch (event.key) {
        case "ArrowDown":
          if (!items.length) return;
          event.preventDefault();
          this.activeIndex = (this.activeIndex + 1) % items.length;
          this.scrollActiveIntoView();
          break;
        case "ArrowUp":
          if (!items.length) return;
          event.preventDefault();
          this.activeIndex =
            this.activeIndex <= 0 ? items.length - 1 : this.activeIndex - 1;
          this.scrollActiveIntoView();
          break;
        case "Enter": {
          const entry = items[this.activeIndex];
          if (!entry) return;
          event.preventDefault();
          this.activateNavItem(entry);
          break;
        }
        case "Escape":
          event.preventDefault();
          this.closePanel();
          break;
      }
    },
    activateNavItem(entry) {
      if (entry.type === "result") {
        this.selectItem(entry.item);
      } else if (entry.type === "browse") {
        this.browseModal();
      } else if (entry.type === "create") {
        this.createNew();
      }
    },
    scrollActiveIntoView() {
      this.$nextTick(() => {
        // Results live in the teleported panel now, not inside `picker`.
        const root = this.$refs.panelOuter;
        const el = root && root.querySelector(".nav-active");
        if (el) el.scrollIntoView({ block: "nearest" });
      });
    },
    browseModal() {
      const self = this;
      self.touch();
      self.closePanel();

      const modalOptions = {
        field: self.field,
        model: self.multiValue ? self.value : [self.value].filter(Boolean),
        maximum: self.field.maximum,
        browserOptions: {
          columns: self.field.columns,
          select: self.field.select,
          lockFilter: self.field.lockFilter,
          dateFilterEnabled: self.dateFilterEnabled,
        },
      };

      self.resolveReferenceType().then(function (referenceType) {
        if (!referenceType) return;

        if (referenceType === "scope") {
          return self.$sdk
            .selectScopes({
              action: "view",
              type: referenceType,
              model: self.multiValue
                ? self.value
                : [self.value].filter(Boolean),
              maximum: self.field.maximum,
              include: self.field.scopeInclude || "scope",
              filter: self.field.lockFilter,
            })
            .then(function (newSelection) {
              self.model = self.multiValue ? newSelection : newSelection[0];
            })
            .catch(function (err) {
              if (err) self.$sdk.error(err);
            });
        }

        self.$sdk
          .browse(referenceType, modalOptions)
          .then(function (newSelection) {
            self.model = self.multiValue ? newSelection : newSelection[0];
          })
          .catch(function (err) {
            if (err) self.$sdk.error(err);
          });
      });
    },
    async swap() {
      const self = this;
      self.touch();

      const referenceType = await self.resolveReferenceType();
      if (!referenceType) return;

      const modalOptions = {
        field: self.field,
        model: [self.value].filter(Boolean),
        maximum: 1,
        browserOptions: {
          columns: self.field.columns,
          select: self.field.select,
          lockFilter: self.field.lockFilter,
          dateFilterEnabled: self.dateFilterEnabled,
        },
      };

      self.$sdk
        .browse(referenceType, modalOptions)
        .then(function (newSelection) {
          self.model = newSelection[0];
        })
        .catch(function (err) {
          if (err) self.$sdk.error(err);
        });
    },
    async resolveReferenceType() {
      if (this.referenceType) return this.referenceType;

      await this.$glossary.ensure();
      const choices = this.$glossary.items.slice().sort(sortByTitle);
      const { key } = await this.$sdk.options(
        choices,
        "Select content type",
        "What type of content are you wanting to select",
      );
      return key;
    },
    async createNew() {
      if (!this.$sdk.global?.create) return;
      this.closePanel();

      const model = this.field.createModel;
      const created = await this.$sdk.global.create(this.referenceType, {
        model,
        preselected: !!model,
      });

      if (created?.multiple) {
        if (this.multiValue) {
          const current = Array.isArray(this.model) ? this.model : [];
          this.model = [...current, ...created.items];
        } else {
          this.model = created.items[0];
        }
        return;
      }

      if (created?._id) {
        if (this.multiValue) {
          const current = Array.isArray(this.model) ? this.model : [];
          this.model = [...current, created];
        } else {
          this.model = created;
        }
      }
    },
    clear() {
      this.model = undefined;
      this.touch();
    },
    remove(element) {
      const current = Array.isArray(this.model) ? this.model : [];
      this.model = current.filter(function (entry) {
        return entry !== element;
      });
      this.touch();
    },
    canEdit(item) {
      if (!this.user) return false;
      return this.$sdk.access.canEditItem(this.user, item);
    },
    async edit(item) {
      const result = await this.$sdk.global
        .edit(item, true)
        .catch(function () {});
      for (const prop in result) {
        item[prop] = result[prop];
      }
    },
    cleanInput(val) {
      if (this.multiValue) {
        if (!val || !Array.isArray(val)) {
          val = [];
        }
        if (this.maximum && val.length > this.maximum) {
          val.length = this.maximum;
        }
      } else if (Array.isArray(val)) {
        val = val[0];
      }
      return val;
    },
  },
};
</script>
<style lang="scss" scoped>
.items {
  border-radius: 0.3em;
  overflow: hidden;
  margin-bottom: 0.5em;

  &:last-child {
    margin-bottom: 0;
  }

  @media (prefers-color-scheme: light) {
    border: 1px solid color-mix(in lab, currentColor 15%, transparent);
  }

  @media (prefers-color-scheme: dark) {
    border: 1px solid color-mix(in lab, currentColor 18%, transparent);
  }

  :deep(.content-item) {
    border: none;
    border-radius: 0;

    @media (prefers-color-scheme: light) {
      border-bottom: 1px solid color-mix(in lab, currentColor 15%, transparent);
    }

    @media (prefers-color-scheme: dark) {
      border-bottom: 1px solid color-mix(in lab, currentColor 18%, transparent);
    }

    &:last-child {
      border-radius: 0.3em;
      border-bottom: none;
    }
  }
}

.multi-list {
  max-height: 450px;
  overflow: auto;
}

.content-picker {
  position: relative;
}

.picker-trigger,
.picker-input-wrap {
  @extend .basic-input-style;

  display: flex;
  align-items: center;
  gap: 0.5em;
  box-sizing: border-box;
  width: 100%;
  height: 2.5em;
  padding: 0 0.75em;
  border-radius: 0.3em;
  font-size: inherit;
  font-family: inherit;
  line-height: 1;
  color: inherit;
  /* background: color-mix(in lab, currentColor 6%, transparent); */
  /* border: 1px solid color-mix(in lab, currentColor 15%, transparent); */
}

.picker-trigger {
  appearance: none;
  cursor: pointer;
  text-align: left;

  &:hover {
    .ux-icon {
      color: var(--primary);
    }
    background: var(--bg1);
    border-color: color-mix(in lab, currentColor 30%, transparent);
  }
}

.picker-trigger-icon,
.picker-input-icon,
.picker-trigger-caret,
.picker-input-spinner {
  /* opacity: 0.5; */
}

.picker-trigger-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.6;
}

.picker-input-wrap {
  background: var(--bg1, #fff);
  border-color: var(--primary);
}

.picker-input {
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  outline: none;
  background: none;
  font: inherit;
  color: inherit;
}

.picker-panel-outer {
  // position / left / top / width / z-index are applied inline via :style,
  // driven by the input's bounding rect — the panel is teleported to <body>
  // so it can escape any overflow:hidden / clipping ancestor (e.g. a modal).
  box-sizing: border-box;
}

.picker-panel {
  /* background: var(--bg1, #fff); */
  /* border: 1px solid color-mix(in lab, currentColor 15%, var(--bg1)); */
  /* border-radius: 0.3em; */
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.picker-browse-lead {
  display: flex;
  align-items: center;
  gap: 0.5em;
  box-sizing: border-box;
  width: 100%;
  padding: 0.6em 0.75em;
  border: none;
  border-bottom: 1px solid color-mix(in lab, currentColor 10%, transparent);
  background: color-mix(in lab, var(--primary) 8%, transparent);
  color: color-mix(in lab, var(--primary) 80%, currentColor);
  font: inherit;
  font-weight: 500;
  text-align: left;
  cursor: pointer;

  span {
    flex: 1;
  }

  &:hover {
    background: color-mix(in lab, var(--primary) 16%, transparent);
  }

  &.nav-active {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }
}

.picker-section-label {
  padding: 0.5em 0.75em 0.25em;
  font-size: 0.72em;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.45;
}

.picker-results {
  max-height: 240px;
  overflow-y: auto;

  :deep(.content-item) {
    border: none;
    border-radius: 0;
    margin-bottom: 0;
    cursor: pointer;
  }

  :deep(.content-item:not(:last-child)) {
    border-bottom: 1px solid color-mix(in lab, currentColor 8%, transparent);
  }

  :deep(.content-item.nav-active) {
    background: color-mix(in lab, var(--primary) 14%, var(--bg1));
  }
}

.picker-status {
  padding: 0.75em;
  font-size: 0.9em;
  opacity: 0.6;
}

.picker-footer {
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0.5em;
  border-top: 1px solid color-mix(in lab, currentColor 10%, transparent);

  :deep(.nav-active) {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }
}
</style>
